import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { ZoomIn, ZoomOut, Maximize2, RefreshCw, FlipHorizontal } from 'lucide-react';

interface ManipulationMatch {
  manipulation_type: string;
  region_x: number | null;
  region_y: number | null;
  region_width: number | null;
  region_height: number | null;
  confidence: number;
  description: string;
  detection_method: string;
}

interface ImageManipulationViewerProps {
  imageUrl: string;
  manipulations: ManipulationMatch[];
  width?: number;
  height?: number;
}

const colorMap: Record<string, string> = {
  splice: 'rgba(255, 0, 0, 0.3)',
  clone: 'rgba(0, 255, 0, 0.3)',
  removal: 'rgba(255, 165, 0, 0.3)',
  addition: 'rgba(0, 0, 255, 0.3)',
  filter: 'rgba(128, 0, 128, 0.3)',
  deepfake: 'rgba(255, 0, 255, 0.3)',
  shadow: 'rgba(100, 100, 100, 0.3)',
  reflection: 'rgba(200, 200, 255, 0.3)',
  intensity: 'rgba(255, 255, 0, 0.3)',
  pattern: 'rgba(0, 255, 255, 0.3)',
  level: 'rgba(128, 128, 0, 0.3)',
  artifact: 'rgba(255, 128, 0, 0.3)',
};

export function ImageManipulationViewer({
  imageUrl,
  manipulations,
  width = 800,
  height = 600,
}: ImageManipulationViewerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const originalCanvasRef = useRef<HTMLCanvasElement>(null);
  const [selectedType, setSelectedType] = useState<string>('all');
  const [imageLoaded, setImageLoaded] = useState(false);
  const [actualWidth, setActualWidth] = useState(width);
  const [actualHeight, setActualHeight] = useState(height);
  const [zoom, setZoom] = useState(1);
  const [showSideBySide, setShowSideBySide] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });

  // Group manipulations by type
  const manipulationsByType = manipulations.reduce((acc, m) => {
    const type = m.manipulation_type;
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(m);
    return acc;
  }, {} as Record<string, ManipulationMatch[]>);

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev * 1.2, 5));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev / 1.2, 0.5));
  };

  const handleResetZoom = () => {
    setZoom(1);
    setPanOffset({ x: 0, y: 0 });
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - panOffset.x, y: e.clientY - panOffset.y });
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDragging) return;
    setPanOffset({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const toggleSideBySide = () => {
    setShowSideBySide(prev => !prev);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    const originalCanvas = originalCanvasRef.current;
    if (!canvas || !imageUrl || !originalCanvas) return;

    const ctx = canvas.getContext('2d');
    const originalCtx = originalCanvas.getContext('2d');
    if (!ctx || !originalCtx) return;

    const image = new Image();
    image.src = imageUrl;
    image.onload = () => {
      const scale = Math.min(width / image.width, height / image.height);
      const newWidth = image.width * scale;
      const newHeight = image.height * scale;

      setActualWidth(newWidth);
      setActualHeight(newHeight);
      canvas.width = newWidth;
      canvas.height = newHeight;
      originalCanvas.width = newWidth;
      originalCanvas.height = newHeight;

      // Draw original image on both canvases
      ctx.drawImage(image, 0, 0, newWidth, newHeight);
      originalCtx.drawImage(image, 0, 0, newWidth, newHeight);
      setImageLoaded(true);
    };
  }, [imageUrl, width, height]);

  useEffect(() => {
    if (!imageLoaded) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear previous overlays
    const image = new Image();
    image.src = imageUrl;
    image.onload = () => {
      ctx.drawImage(image, 0, 0, actualWidth, actualHeight);

      // Draw manipulation overlays
      manipulations.forEach(manipulation => {
        if (
          selectedType === 'all' ||
          selectedType === manipulation.manipulation_type
        ) {
          if (
            manipulation.region_x !== null &&
            manipulation.region_y !== null &&
            manipulation.region_width !== null &&
            manipulation.region_height !== null
          ) {
            const color = colorMap[manipulation.manipulation_type] || 'rgba(255, 0, 0, 0.3)';
            ctx.fillStyle = color;
            ctx.fillRect(
              (manipulation.region_x * actualWidth) / 100,
              (manipulation.region_y * actualHeight) / 100,
              (manipulation.region_width * actualWidth) / 100,
              (manipulation.region_height * actualHeight) / 100
            );

            // Draw border
            ctx.strokeStyle = color.replace('0.3', '1');
            ctx.lineWidth = 2;
            ctx.strokeRect(
              (manipulation.region_x * actualWidth) / 100,
              (manipulation.region_y * actualHeight) / 100,
              (manipulation.region_width * actualWidth) / 100,
              (manipulation.region_height * actualHeight) / 100
            );

            // Add label
            ctx.fillStyle = 'white';
            ctx.strokeStyle = 'black';
            ctx.lineWidth = 3;
            ctx.font = '14px sans-serif';
            const label = `${manipulation.manipulation_type} (${Math.round(manipulation.confidence * 100)}%)`;
            const labelX = (manipulation.region_x * actualWidth) / 100;
            const labelY = ((manipulation.region_y * actualHeight) / 100) - 5;
            ctx.strokeText(label, labelX, labelY);
            ctx.fillText(label, labelX, labelY);
          }
        }
      });
    };
  }, [imageLoaded, selectedType, manipulations, imageUrl, actualWidth, actualHeight]);

  const manipulationTypes = Object.keys(manipulationsByType);

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>Manipulation Detection Visualization</CardTitle>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleZoomIn}>
            <ZoomIn className="h-4 w-4 mr-1" />
            Zoom In
          </Button>
          <Button variant="outline" size="sm" onClick={handleZoomOut}>
            <ZoomOut className="h-4 w-4 mr-1" />
            Zoom Out
          </Button>
          <Button variant="outline" size="sm" onClick={handleResetZoom}>
            <Maximize2 className="h-4 w-4 mr-1" />
            Reset
          </Button>
          <Button variant="outline" size="sm" onClick={toggleSideBySide}>
            <FlipHorizontal className="h-4 w-4 mr-1" />
            {showSideBySide ? 'Single View' : 'Side by Side'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="all" className="w-full">
          <TabsList className="flex flex-wrap gap-2 mb-4">
            <TabsTrigger
              value="all"
              onClick={() => setSelectedType('all')}
              className={selectedType === 'all' ? 'bg-primary text-primary-foreground' : ''}
            >
              All
              <Badge variant="secondary" className="ml-2">
                {manipulations.length}
              </Badge>
            </TabsTrigger>
            {manipulationTypes.map(type => (
              <TabsTrigger
                key={type}
                value={type}
                onClick={() => setSelectedType(type)}
                className={selectedType === type ? 'bg-primary text-primary-foreground' : ''}
              >
                {type}
                <Badge variant="secondary" className="ml-2">
                  {manipulationsByType[type].length}
                </Badge>
              </TabsTrigger>
            ))}
          </TabsList>

          <div className={`relative border rounded-lg overflow-hidden ${showSideBySide ? 'grid grid-cols-2 gap-4' : ''}`}>
            {showSideBySide && (
              <div className="relative">
                <canvas
                  ref={originalCanvasRef}
                  style={{
                    maxWidth: '100%',
                    height: 'auto',
                    transform: `scale(${zoom})`,
                    transformOrigin: '0 0',
                    cursor: isDragging ? 'grabbing' : 'grab'
                  }}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                />
                <div className="absolute top-2 left-2">
                  <Badge>Original</Badge>
                </div>
              </div>
            )}
            <div className="relative">
              <canvas
                ref={canvasRef}
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                  transform: `scale(${zoom}) translate(${panOffset.x}px, ${panOffset.y}px)`,
                  transformOrigin: '0 0',
                  cursor: isDragging ? 'grabbing' : 'grab'
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              />
              {showSideBySide && (
                <div className="absolute top-2 left-2">
                  <Badge>Detected Manipulations</Badge>
                </div>
              )}
            </div>
          </div>

          <div className="mt-4">
            <h4 className="font-semibold mb-2">Detection Details</h4>
            <div className="space-y-2">
              {manipulations
                .filter(m => selectedType === 'all' || selectedType === m.manipulation_type)
                .map((m, i) => (
                  <div
                    key={i}
                    className="p-2 border rounded-md"
                    style={{
                      borderLeft: `4px solid ${colorMap[m.manipulation_type]?.replace('0.3', '1') || 'red'}`
                    }}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">{m.manipulation_type}</p>
                        <p className="text-sm text-muted-foreground">{m.description}</p>
                      </div>
                      <Badge variant="secondary">
                        {Math.round(m.confidence * 100)}% confidence
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Detected using: {m.detection_method}
                    </p>
                  </div>
                ))}
            </div>
          </div>
        </Tabs>
      </CardContent>
    </Card>
  );
} 